import type { QuoteFormPreset } from "./types";

/**
 * Map an InsuraCentral lead row to QuoteForm preset values. Tolerates the
 * camelCase + snake_case dual naming the CRM uses across older + newer code
 * paths (Sequelize models on the backend can emit either depending on the
 * route). Anything missing stays `null` — the form just leaves that field
 * blank for the agent to fill.
 *
 * NOTE: `height` on a lead is stored as inches (number) in most rows but
 * a small subset have it as a free-text string like `5'10"`. We only adopt
 * the numeric form here — parsing the string form lives in `lead-bio.util`
 * on the backend and we'd rather let the agent re-enter than guess wrong.
 */
export function leadToPreset(lead: any | null | undefined): QuoteFormPreset {
  if (!lead) return {};

  const name =
    lead.name ||
    [lead.firstName ?? lead.first_name, lead.lastName ?? lead.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

  const birthDate = normalizeBirthDate(lead.dob || lead.birthday || lead.birthDate);

  const gender =
    typeof lead.gender === "string"
      ? lead.gender.trim().toUpperCase().startsWith("F")
        ? "F"
        : lead.gender.trim().toUpperCase().startsWith("M")
        ? "M"
        : null
      : null;

  const heightInches = parseLeadHeight(lead.height);

  const weightPounds = parseLeadWeight(lead.weight);

  // Tobacco isn't reliably on every lead row — API-imported leads carry it
  // in custom_fields — but when it is anywhere, we adopt it.
  const cf = lead.customFields || lead.custom_fields || {};
  const tobaccoRaw =
    lead.tobaccoUse ?? lead.tobacco ?? cf.tobacco_use ?? cf.tobaccoUse ?? null;
  const tobaccoUse =
    typeof tobaccoRaw === "boolean"
      ? tobaccoRaw
      : tobaccoRaw === "true"
        ? true
        : tobaccoRaw === "false"
          ? false
          : null;

  // ZIP may be stored as zip+4 ("48187-1234") — the form validates 5 digits.
  const zipMatch = String(lead.zipCode || lead.zip_code || "").match(/\d{5}/);

  return {
    leadId: lead.id || null,
    leadName: name || null,
    birthDate,
    gender,
    state: lead.state || null,
    zipCode: zipMatch ? zipMatch[0] : null,
    heightInches,
    weightPounds,
    tobaccoUse,
  };
}

/**
 * Lead height/weight live in VARCHAR columns — the API hands us strings
 * (`5'10"`, `"175"`), older rows may be numbers, and hand-typed data has
 * typos like `5'6""`. Normalize height to total inches and weight to
 * pounds; null on garbage (agent re-enters rather than us guessing).
 */
export function parseLeadHeight(raw: any): number | null {
  if (raw === null || raw === undefined) return null;
  // Normalize smart quotes and coerce legacy numeric rows to text — a stored
  // 5.11 must be read as the STRING "5.11" so the decimal survives.
  const s = String(raw)
    .trim()
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[”“]/g, '"');
  if (!s) return null;

  // 1. Feet + inches. Tolerates doubled/missing quotes and word forms:
  //    5'10"  ·  5'10  ·  5'6""  ·  5 ft 10 in  ·  5ft10
  const ftIn = s.match(
    // Trailing unit is optional and quote marks may be doubled: 5'6"" · 5'6" · 5'6
    /^(\d{1,2})\s*(?:'|ft\.?|feet)\s*(\d{1,2})?\s*(?:in\.?|inch(?:es)?)?\s*["']*\s*$/,
  );
  if (ftIn) {
    const ft = Number(ftIn[1]);
    const inch = ftIn[2] != null ? Number(ftIn[2]) : 0;
    if (ft >= 3 && ft <= 8 && inch >= 0 && inch <= 11) return ft * 12 + inch;
    return null;
  }

  // 2. FEET.INCHES — the dominant production format (11,829 of 15,975 prod
  //    rows as of 2026-07-28). The decimal is LITERAL INCHES, not tenths of a
  //    foot: "5.11" is 5'11" (71"), NOT 5.11ft (61"). Proven by the stored
  //    distribution — decimal parts run 1-9 and 11, and never exceed 11.
  //    Reading these as fractional feet put 5'11" leads in at 5'1".
  const dec = s.match(/^([3-8])\s*\.\s*(\d{1,2})$/);
  if (dec) {
    const ft = Number(dec[1]);
    const inch = Number(dec[2]);
    if (inch <= 11) return ft * 12 + inch;
    // >11 can't be inches, so this one really is fractional feet (5.75 = 5¾').
    const total = Math.round(Number(s) * 12);
    return total >= 36 && total <= 96 ? total : null;
  }

  // 3. Bare number: 36-96 = inches, 3-8 = whole feet ("5" = 5'0", 983 prod rows).
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  if (n >= 36 && n <= 96) return Math.round(n);
  if (n >= 3 && n <= 8) return Math.round(n) * 12;
  return null;
}

export function parseLeadWeight(raw: any): number | null {
  const n =
    typeof raw === "number"
      ? raw
      : typeof raw === "string"
        ? Number(raw.trim().replace(/\s*(lbs?|pounds?)\.?$/i, ""))
        : NaN;
  return Number.isFinite(n) && n >= 40 && n <= 1000 ? Math.round(n) : null;
}

/**
 * Coerce whatever the lead model returns into a YYYY-MM-DD string the
 * `<input type="date">` element accepts. Returns null on garbage so the
 * form shows an empty date picker rather than `Invalid Date`.
 */
function normalizeBirthDate(raw: any): string | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    // Already ISO-ish? Strip time portion if present.
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) return isoMatch[1];
    // Try Date parsing as a fallback.
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return raw.toISOString().slice(0, 10);
  }
  return null;
}
